import { useEffect } from 'react';

/**
 * One consistent action-success affordance across the admin surface (#6 polish):
 * a fixed, auto-dismissing snackbar. Controlled — the parent holds the message and
 * clears it via onClose. kind 'success' (default) | 'error'. Purely presentational;
 * no business logic, no live-protection surface.
 */
export interface ToastState {
  message: string;
  kind?: 'success' | 'error';
}

export function Toast({ toast, onClose, ms = 4000 }: { toast: ToastState | null; onClose: () => void; ms?: number }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, ms);
    return () => clearTimeout(t);
  }, [toast, onClose, ms]);

  if (!toast) return null;
  const err = toast.kind === 'error';
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded px-4 py-2.5 text-sm font-medium text-white shadow-lg ${
        err ? 'bg-danger-bright' : 'bg-success-bright'
      }`}
    >
      <span aria-hidden>{err ? '⚠️' : '✓'}</span>
      <span>{toast.message}</span>
      <button onClick={onClose} aria-label="Dismiss" className="ml-1 text-white/70 hover:text-white">
        ✕
      </button>
    </div>
  );
}
